import React from 'react';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { useBuilderStore } from '../../../store/checkoutBuilder/BuilderContext';
import type { ColumnNode, ComponentNode, RowNode } from '../../../store/checkoutBuilder/types';
import { renderComponent } from '../renderer';
import { getColumnWidthClass } from '../../../utils/gridUtils';

const selectionClasses = (selected: boolean, isMobile: boolean = false) =>
  selected ? (isMobile ? 'outline outline-1 outline-blue-400' : 'outline outline-2 outline-blue-500') : '';

interface Props {
  rowId: string;
  emptyHint?: string;
  fullHeight?: boolean; // Propriedade para controlar se o placeholder deve ocupar altura total
};

const StageZoneCanvas: React.FC<Props> = ({ rowId, emptyHint, fullHeight = false }) => {
  const { state, selectNode, deleteNode, updateComponentProps, addComponent } = useBuilderStore();
  const { document, selectionId } = state;

  const row = document.nodes[rowId] as RowNode | undefined;

  // Se não houver linha, não mostrar nada
  if (!row) {
    return null;
  }

  // Determinar o breakpoint atual
  const breakpoint = state.ui?.breakpoint || 'desktop';
  const isMobile = breakpoint === 'mobile';

  // Renderiza uma coluna individual
  const renderColumn = (colId: string) => {
    const col = document.nodes[colId] as ColumnNode | undefined;
    if (!col || col.type !== 'column') return null;

    // Obter a classe de largura para esta coluna com base no breakpoint atual
    const widthClass = getColumnWidthClass(col.props.widthByBp?.[breakpoint] || 12);

    return (
      <div
        key={colId}
        className={`${widthClass} px-4`}
      >
        <Droppable droppableId={`col:${col.id}`} direction="vertical">
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`w-full transition-all rounded-sm flex flex-col ${col.children.length === 0
                ? 'justify-center items-center min-h-[130px]'
                : 'gap-3 py-4'
                } ${snapshot.isDraggingOver ? 'bg-blue-50/50' : ''}`}
              onClick={() => selectNode(null)}
            >
              {snapshot.isDraggingOver && (
                <div className={`text-center font-medium py-4 ${col.children.length === 0 ? 'text-blue-600' : 'text-gray-600'
                  }`}>
                  Solte aqui
                </div>
              )}
              {col.children.length === 0 && !snapshot.isDraggingOver && (
                <div className={`w-full ${fullHeight ? 'h-full min-h-[900px]' : 'min-h-[100px]'} ${isMobile ? 'border border-dashed border-blue-300 bg-blue-50/20 rounded px-3 py-2' : 'border-2 border-dashed border-blue-400 bg-blue-50/30 rounded-lg px-4 py-2'} flex items-center justify-center text-blue-500 text-xs font-medium`}>
                  {emptyHint || "Arraste componentes aqui"}
                </div>
              )}
              {col.children.map((cmpId, index) => {
                const cmp = document.nodes[cmpId] as ComponentNode | undefined;
                if (!cmp || cmp.type !== 'component') return null;
                return (
                  <Draggable key={cmp.id} draggableId={cmp.id} index={index}>
                    {(dragProvided) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                        className={`group relative w-full flex flex-col ${isMobile ? 'hover:ring-1 hover:ring-gray-300' : 'hover:ring-2 hover:ring-gray-300'} ${selectionClasses(selectionId === cmp.id, isMobile)}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          selectNode(cmp.id);
                        }}
                      >
                        {renderComponent(cmp, {
                          selected: selectionId === cmp.id,
                          onSelect: () => selectNode(cmp.id),
                          onUpdateProps: (patch) => updateComponentProps(cmp.id, patch),
                          dragHandleProps: undefined,
                          onDelete: () => deleteNode(cmp.id),
                          onDuplicate: () => {
                            // insert a clone right after current index
                            addComponent(col.id, cmp.compType, index + 1, { ...(cmp.props || {}) });
                          },
                        })}
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </div>
    );
  };

  // Se for a área lateral ou footer mobile, renderizar apenas a primeira coluna
  if (rowId === 'side-area-row' || rowId === 'mobile-footer-row') {
    const firstColId = row.children[0];
    if (!firstColId) return null;
    return renderColumn(firstColId);
  }

  // Para outras áreas, renderizar todas as colunas lado a lado
  return (
    <div className="flex flex-wrap w-full -mx-1">
      {row.children.map((colId) => renderColumn(colId))}
    </div>
  );
};

export default StageZoneCanvas;
